import { Suspense } from "react";
import { useAtomValue } from "jotai";
import {
  isViewOnlyModeAtom,
  isZapErrorObject,
  zapMetadataAtomFamily_SUSPENDS,
} from "@zapier/canvas-state";
import {
  Alert,
  Icon,
  SkeletonBlock,
  SkeletonText,
  Spacer,
  Text,
  TooltipWrapper,
} from "@zapier/design-system";

import { ZapPreview } from "../../ZapPreview";
import { AvatarWithName } from "../AvatarWithName";
import { ZapNotAvailable } from "../ZapNotAvailable";
import zapPreviewStyles from "../../ZapPreview/ZapPreview.module.css";

import styles from "./ZapPreviewContainer.module.css";

export function ZapPreviewContainer({
  onRemoveZap,
  zapId,
}: {
  onRemoveZap?: () => void;
  zapId: string;
}) {
  return (
    <Suspense fallback={<LoadingState />}>
      <ZapPreviewContainerSuspended onRemoveZap={onRemoveZap} zapId={zapId} />
    </Suspense>
  );
}

function LoadingState() {
  return (
    <div
      className={`${zapPreviewStyles.zapPreview} ${zapPreviewStyles.loading}`}
    >
      <div className={styles.zapPreviewIconsRow}>
        {[{ id: "1" }, { id: "2" }].map((step) => (
          <span key={step.id}>
            <SkeletonBlock borderRadius="medium" height="40px" width="40px" />
          </span>
        ))}
      </div>
      <SkeletonText textAlign="left" lines={1} size="heading" width="100%" />
      <Spacer height={10} />
      <SkeletonText textAlign="left" lines={2} size="heading" width="100%" />
    </div>
  );
}

export function ZapPreviewContainerSuspended({
  onRemoveZap,
  zapId,
}: {
  onRemoveZap?: () => void;
  zapId: string;
}) {
  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);
  const zapData = useAtomValue(zapMetadataAtomFamily_SUSPENDS(zapId));
  if (!zapData) {
    return null;
  }
  if (isZapErrorObject(zapData)) {
    return <ZapNotAvailable />;
  }
  const zap = zapData;
  const steps = zap.steps;
  const isPublished = zap.is_published;

  return (
    <div className={styles.sectionWrapper}>
      {!isViewOnlyMode && !isPublished && (
        <Alert status="warning">
          Publish this Zap if you want to make the steps of this Zap
          connectable.
        </Alert>
      )}
      <Text type="SmallPrint3">Zap</Text>
      <div className={styles.avatarRow}>
        <AvatarWithName ownerId={zap.owner_customuser_id} />
        {onRemoveZap && (
          <div className={styles.cancelButtonWrapper}>
            <TooltipWrapper
              position="west"
              content={isViewOnlyMode ? "" : "Remove Zap"}
            >
              {() => (
                <RemoveZapButton
                  disabled={isViewOnlyMode}
                  onCancel={onRemoveZap}
                />
              )}
            </TooltipWrapper>
          </div>
        )}
      </div>
      <ZapPreview
        hasZapNotes={zap.has_zap_note}
        title={zap?.title}
        steps={steps}
        tagLabel={isPublished ? undefined : "Draft"}
        zapId={zapId}
      />
    </div>
  );
}

type CancelButtonProps = {
  disabled: boolean;
  onCancel: () => void;
};

const RemoveZapButton = ({ onCancel, disabled }: CancelButtonProps) => {
  return (
    <button
      className={styles.buttonNoStyle}
      aria-label="Remove Zap"
      onClick={onCancel}
      type="button"
      disabled={disabled}
    >
      <Icon name="formXCircle" size={24} color="GrayWarm5" />
    </button>
  );
};
